import React, { useState } from 'react';
import { AdminPageLayout } from '@/components/admin/shared/AdminPageLayout';
import { ServiceFilter } from '@/components/controls/ServiceFilter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { 
  Plus, 
  Bot, 
  Activity, 
  Users, 
  MessageSquare,
  TrendingUp, 
  TrendingDown, 
  Circle,
  AlertTriangle,
  CheckCircle,
  Clock,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Play,
  Pause,
  Settings
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface BotData {
  id: string;
  name: string;
  client: string;
  status: 'active' | 'paused' | 'error' | 'maintenance';
  model: 'gpt-4' | 'gpt-3.5' | 'claude-3' | 'claude-2';
  conversations: number;
  monthlyTrend: number;
  successRate: number;
  avgResponseTime: number;
  lastActive: string;
  createdDate: string;
  cost: number;
  uptime: number;
  description: string;
  category: 'customer-service' | 'sales' | 'support' | 'analytics';
}

export function AdminBots() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [modelFilter, setModelFilter] = useState('all');

  const bots: BotData[] = [
    {
      id: '1',
      name: 'CustomerCare Pro',
      client: 'TechCorp Solutions',
      status: 'active',
      model: 'gpt-4',
      conversations: 8420,
      monthlyTrend: 15,
      successRate: 94,
      avgResponseTime: 1.2,
      lastActive: '2 min',
      createdDate: '2024-01-20',
      cost: 1250,
      uptime: 99.8,
      description: 'Bot de atención al cliente con IA avanzada',
      category: 'customer-service'
    },
    {
      id: '2',
      name: 'SalesAssist AI',
      client: 'StartupX Innovations',
      status: 'active',
      model: 'gpt-3.5',
      conversations: 3210,
      monthlyTrend: 8,
      successRate: 89,
      avgResponseTime: 0.8,
      lastActive: '5 min',
      createdDate: '2024-02-15',
      cost: 680,
      uptime: 98.5,
      description: 'Asistente de ventas automatizado',
      category: 'sales'
    },
    {
      id: '3',
      name: 'TechSupport Elite',
      client: 'BigCorp Enterprise',
      status: 'error',
      model: 'claude-3',
      conversations: 12800,
      monthlyTrend: -5,
      successRate: 78,
      avgResponseTime: 2.1,
      lastActive: '2 hours',
      createdDate: '2023-11-10',
      cost: 1890,
      uptime: 95.2,
      description: 'Soporte técnico especializado nivel enterprise',
      category: 'support'
    },
    {
      id: '4',
      name: 'HealthBot Assistant',
      client: 'HealthFirst Medical',
      status: 'active',
      model: 'gpt-4',
      conversations: 5670,
      monthlyTrend: 22,
      successRate: 96,
      avgResponseTime: 1.5,
      lastActive: '1 min',
      createdDate: '2024-02-01',
      cost: 1420,
      uptime: 99.9,
      description: 'Asistente médico con protocolos especializados',
      category: 'customer-service'
    },
    {
      id: '5',
      name: 'Analytics Insight',
      client: 'BigCorp Enterprise',
      status: 'paused',
      model: 'claude-2',
      conversations: 890,
      monthlyTrend: -15,
      successRate: 85,
      avgResponseTime: 3.2,
      lastActive: '1 day',
      createdDate: '2024-01-05',
      cost: 450,
      uptime: 97.1,
      description: 'Bot de análisis de datos y reportes',
      category: 'analytics'
    },
    {
      id: '6',
      name: 'EduGuide Tutor',
      client: 'EduLearn Platform',
      status: 'maintenance',
      model: 'gpt-3.5',
      conversations: 4320,
      monthlyTrend: 3,
      successRate: 91,
      avgResponseTime: 1.8,
      lastActive: '6 hours',
      createdDate: '2024-03-01',
      cost: 780,
      uptime: 98.8,
      description: 'Tutor educativo personalizado',
      category: 'customer-service'
    }
  ];

  const filteredBots = bots
    .filter(bot => statusFilter === 'all' || bot.status === statusFilter)
    .filter(bot => modelFilter === 'all' || bot.model === modelFilter)
    .filter(bot => 
      bot.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bot.client.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'text-green-600 bg-green-50 border-green-200',
      paused: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      error: 'text-red-600 bg-red-50 border-red-200',
      maintenance: 'text-blue-600 bg-blue-50 border-blue-200'
    };
    return colors[status as keyof typeof colors] || colors.active;
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      active: CheckCircle,
      paused: Pause,
      error: AlertTriangle,
      maintenance: Settings
    };
    const Icon = icons[status as keyof typeof icons] || CheckCircle;
    return Icon;
  };

  const getModelColor = (model: string) => {
    const colors = {
      'gpt-4': 'bg-purple-100 text-purple-800',
      'gpt-3.5': 'bg-blue-100 text-blue-800',
      'claude-3': 'bg-orange-100 text-orange-800',
      'claude-2': 'bg-gray-100 text-gray-800'
    };
    return colors[model as keyof typeof colors] || colors['gpt-3.5'];
  };

  const getCategoryIcon = (category: string) => {
    const icons = {
      'customer-service': Users,
      'sales': TrendingUp,
      'support': Bot,
      'analytics': Activity
    };
    return icons[category as keyof typeof icons] || Bot;
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const statusCounts = {
    active: bots.filter(b => b.status === 'active').length,
    paused: bots.filter(b => b.status === 'paused').length,
    error: bots.filter(b => b.status === 'error').length,
    maintenance: bots.filter(b => b.status === 'maintenance').length
  };

  const totalConversations = bots.reduce((sum, bot) => sum + bot.conversations, 0);
  const avgSuccessRate = bots.reduce((sum, bot) => sum + bot.successRate, 0) / bots.length;
  const totalCost = bots.reduce((sum, bot) => sum + bot.cost, 0);
  const avgUptime = bots.reduce((sum, bot) => sum + bot.uptime, 0) / bots.length;

  return (
    <AdminPageLayout
      title="Gestión de Bots"
      subtitle={`${bots.length} bots en total • ${statusCounts.active} activos • ${statusCounts.error} con problemas`}
      actions={
        <>
          <Button variant="outline" size="sm">
            <Activity className="w-4 h-4 mr-2" />
            Monitoreo
          </Button>
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Bot
          </Button>
        </>
      }
      filters={
        <>
          <Input
            placeholder="Buscar bots o clientes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="paused">Pausados</SelectItem>
              <SelectItem value="error">Con Error</SelectItem>
              <SelectItem value="maintenance">Mantenimiento</SelectItem>
            </SelectContent>
          </Select>
          <Select value={modelFilter} onValueChange={setModelFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Modelo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="gpt-4">GPT-4</SelectItem>
              <SelectItem value="gpt-3.5">GPT-3.5</SelectItem>
              <SelectItem value="claude-3">Claude 3</SelectItem>
              <SelectItem value="claude-2">Claude 2</SelectItem>
            </SelectContent>
          </Select>
        </>
      }
    >
      {/* Métricas generales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conversaciones</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatNumber(totalConversations)}
                </p>
              </div>
              <MessageSquare className="h-8 w-8 text-blue-600 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tasa de Éxito</p>
                <p className="text-2xl font-bold text-green-600">
                  {avgSuccessRate.toFixed(1)}%
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Costo Total</p>
                <p className="text-2xl font-bold text-orange-600">
                  ${formatNumber(totalCost)}
                </p>
              </div>
              <Activity className="h-8 w-8 text-orange-600 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Uptime Promedio</p>
                <p className="text-2xl font-bold text-purple-600">
                  {avgUptime.toFixed(1)}%
                </p>
              </div>
              <Bot className="h-8 w-8 text-purple-600 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Grid de bots */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBots.map((bot) => {
          const StatusIcon = getStatusIcon(bot.status);
          const CategoryIcon = getCategoryIcon(bot.category);
          
          return (
            <Card key={bot.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white">
                      <Bot className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{bot.name}</CardTitle>
                      <CardDescription className="flex items-center gap-1">
                        <CategoryIcon className="h-3 w-3" />
                        {bot.client}
                      </CardDescription>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem>
                        <Eye className="w-4 h-4 mr-2" />
                        Ver Detalles
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Edit className="w-4 h-4 mr-2" />
                        Configurar
                      </DropdownMenuItem>
                      {bot.status === 'active' ? (
                        <DropdownMenuItem>
                          <Pause className="w-4 h-4 mr-2" />
                          Pausar
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem>
                          <Play className="w-4 h-4 mr-2" />
                          Activar
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Estado y modelo */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StatusIcon className={`h-4 w-4 ${getStatusColor(bot.status).split(' ')[0]}`} />
                    <Badge className={getStatusColor(bot.status)}>
                      {bot.status === 'active' && 'Activo'}
                      {bot.status === 'paused' && 'Pausado'}
                      {bot.status === 'error' && 'Error'}
                      {bot.status === 'maintenance' && 'Mantenimiento'}
                    </Badge>
                  </div>
                  <Badge className={getModelColor(bot.model)}>
                    {bot.model.toUpperCase()}
                  </Badge>
                </div>

                {/* Descripción */}
                <p className="text-sm text-muted-foreground">{bot.description}</p>

                {/* Métricas de rendimiento */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Tasa de Éxito</span>
                    <div className="flex items-center gap-2">
                      <Progress value={bot.successRate} className="w-16 h-2" />
                      <span className="font-medium">{bot.successRate}%</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Uptime</span>
                    <div className="flex items-center gap-2">
                      <Progress value={bot.uptime} className="w-16 h-2" />
                      <span className="font-medium">{bot.uptime}%</span>
                    </div>
                  </div>
                </div>

                {/* Métricas principales */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Conversaciones</p>
                    <p className="font-medium">{formatNumber(bot.conversations)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Costo/mes</p>
                    <p className="font-medium text-orange-600">${formatNumber(bot.cost)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tiempo Resp.</p>
                    <p className="font-medium">{bot.avgResponseTime}s</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tendencia</p>
                    <div className="flex items-center gap-1">
                      {bot.monthlyTrend > 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-600" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-red-600" />
                      )}
                      <span className={`text-xs ${bot.monthlyTrend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {bot.monthlyTrend > 0 ? '+' : ''}{bot.monthlyTrend}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Información adicional */}
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                  <div className="flex items-center gap-1">
                    <Circle className="h-2 w-2 fill-current" />
                    <span>Último: {bot.lastActive}</span>
                  </div>
                  <span>Creado: {new Date(bot.createdDate).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Resumen por rendimiento */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-green-600" />
              Top Performers
            </CardTitle>
            <CardDescription>
              Bots con mejor rendimiento este mes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {bots
                .sort((a, b) => b.successRate - a.successRate)
                .slice(0, 3)
                .map((bot, index) => (
                  <div key={bot.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0 ? 'bg-yellow-100 text-yellow-800' :
                        index === 1 ? 'bg-gray-100 text-gray-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium">{bot.name}</div>
                        <div className="text-sm text-muted-foreground">{bot.client}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-green-600">{bot.successRate}%</div>
                      <div className="text-sm text-muted-foreground">éxito</div>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Requieren Atención
            </CardTitle>
            <CardDescription>
              Bots con problemas o bajo rendimiento
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {bots
                .filter(bot => bot.status === 'error' || bot.successRate < 85 || bot.uptime < 98)
                .slice(0, 3)
                .map((bot) => (
                  <div key={bot.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <div>
                        <div className="font-medium">{bot.name}</div>
                        <div className="text-sm text-red-600">
                          {bot.status === 'error' ? 'Error en el sistema' :
                           bot.successRate < 85 ? `Éxito: ${bot.successRate}%` :
                           `Uptime: ${bot.uptime}%`}
                        </div>
                      </div>
                    </div>
                    <Button size="sm" variant="outline">
                      Revisar
                    </Button>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminPageLayout>
  );
}